/**
 * BulkEventBuilder — Summer Swim Club permutation generator (Feb 2026).
 *
 * Solves the "100y IM × 5 age groups × 3 genders = 15 manual taps"
 * tedium by letting a coach pick:
 *   1. Distance + Stroke    (one event name)
 *   2. Genders               (multi-select chips: Girls / Boys / Mixed)
 *   3. Age Groups            (multi-select chips: 8&U, 9-10, 11-12,
 *                             13-14, 15-18, + free-form custom tags)
 * then taps a single "Add X Events" button that pushes X flat
 * `MeetEvent` rows (gender_count × age_group_count) into the parent's
 * events state. Loop order is age-group OUTER, gender INNER so the
 * generated list reads sequentially as "9-10 Girls, 9-10 Boys,
 * 11-12 Girls, 11-12 Boys, …" — the rhythm coaches use to script a
 * heat sheet.
 *
 * CONTRACT: this component is PURELY a frontend multiplier. It does
 * NOT alter sportConfig.js, the schedule engine, or the backend
 * MeetEvent model. Each generated event is a standard row matching
 * the same shape the predefined picker emits. Swim event tiles are
 * single-gendered (mutually exclusive Girls / Boys / Mixed) so every
 * row this builder emits stamps EXACTLY ONE of showGirls / showBoys /
 * combined as true and the other two as false.
 *
 * Each generated row pulls its `default_duration_seconds` /
 * `default_heats` from the user's saved Settings (or the documented
 * SWIM_*_BASELINES defaults) — youth bracket maps to youth baselines,
 * older bracket maps to senior baselines. Custom age tags default to
 * senior pacing since most cover both sides of the divide.
 */
import React, { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  SWIM_SENIOR_BASELINES,
  SWIM_YOUTH_BASELINES,
  getDistanceSuffix,
  normalizePoolUnit,
  secondsToMMSS,
} from '../../utils/sportConfig';
import { getSettings } from '../../utils/settings';

// Available distances. 500 maps to '500y' / '400m' (NFHS swap is
// handled in the label generator below since the user thinks of it
// as a single "distance free" event).
const DISTANCE_OPTIONS = [25, 50, 100, 200, 500];

// Stroke / event-name catalog. Relay strokes are flagged so the
// baseline lookup picks the relay baselines.
const STROKE_OPTIONS = [
  { value: 'Freestyle',       label: 'Freestyle',       isRelay: false, isIM: false },
  { value: 'Backstroke',      label: 'Backstroke',      isRelay: false, isIM: false },
  { value: 'Breaststroke',    label: 'Breaststroke',    isRelay: false, isIM: false },
  { value: 'Butterfly',       label: 'Butterfly',       isRelay: false, isIM: false },
  { value: 'IM',              label: 'IM',              isRelay: false, isIM: true  },
  { value: 'Freestyle Relay', label: 'Freestyle Relay', isRelay: true,  isIM: false },
  { value: 'Medley Relay',    label: 'Medley Relay',    isRelay: true,  isIM: false },
];

// Gender chip catalog — Swim event tiles are mutually exclusive
// (Girls OR Boys OR Mixed), so the bulk builder generates one row
// per selected gender × age-group permutation. Each emitted row
// stamps exactly one of showGirls / showBoys / combined as true.
const GENDER_OPTIONS = ['Girls', 'Boys', 'Mixed'];

// Map a builder gender chip to the event-row flag triplet. Kept as
// a static dictionary so the loop body stays a one-liner and the
// invariant (exactly one flag true) is grep-able.
const GENDER_FLAGS = {
  Girls: { showGirls: true,  showBoys: false, combined: false },
  Boys:  { showGirls: false, showBoys: true,  combined: false },
  Mixed: { showGirls: false, showBoys: false, combined: true  },
};

// Age-group catalogs — two flavors keyed by `swim_preset`:
//   • summer_club  → numeric youth brackets (USA-Swimming age-group meets)
//   • standard_hs  → scholastic divisions (NFHS dual-meet vocabulary)
// The active list flips at mount time and the BulkEventBuilder is
// `key`-remounted by the parent whenever `swim_preset` changes, so
// stale chip selections never cross over between modes.

const YOUTH_CLUB_AGE_GROUPS = ['8 & Under', '9-10', '11-12', '13-14', '15-18'];
const SCHOLASTIC_DIVISIONS = ['Varsity', 'JV', 'Freshman'];

// Age groups that map to youth pacing baselines. 11-12 swimmers in
// summer-club meets still need the youth pacing buffers (slower
// transitions, "No Time" reseed overhead, smaller heats), so they
// route to youth keys alongside 8&U and 9-10. 13-14 and 15-18 are
// fast enough to take the senior baselines. Scholastic chips
// (Varsity / JV / Freshman) and free-form custom tags fall through
// to senior pacing because high-school swimmers + unknown labels
// both want the tighter dual-meet rhythm.
const YOUTH_AGE_GROUPS = new Set(['8 & Under', '9-10', '11-12']);

/**
 * Build the human-readable event name. Handles the NFHS 500→400 swap
 * (distance-free in meters is 400m) so the bulk-builder reflects the
 * same rule the preset generators apply.
 */
function buildEventName(distance, stroke, poolUnit) {
  const u = getDistanceSuffix(poolUnit);
  const renderedDistance = (distance === 500 && normalizePoolUnit(poolUnit) === 'meters') ? 400 : distance;
  return `${renderedDistance}${u} ${stroke}`;
}

/**
 * Pick the settings key for the (distance, isRelay, ageGroup) combo.
 * Returns one of the 9 documented baseline ids — never returns null
 * so the caller can always read `settings[`${key}_duration`]` etc.
 */
function pickBaselineKey({ distance, isRelay, ageGroup }) {
  const isYouth = YOUTH_AGE_GROUPS.has(ageGroup);
  if (isRelay) {
    return isYouth ? 'swim_youth_relay' : 'swim_senior_200_relay';
  }
  if (isYouth) {
    if (distance <= 25) return 'swim_youth_25_sprint';
    if (distance <= 50) return 'swim_youth_50_sprint';
    return 'swim_youth_100';
  }
  if (distance <= 50) return 'swim_senior_50_sprint';
  if (distance <= 100) return 'swim_senior_100';
  if (distance <= 200) return 'swim_senior_200';
  return 'swim_senior_500_distance';
}

// Pre-index the baseline rows by id for O(1) default lookups.
const BASELINE_INDEX = (() => {
  const out = {};
  [...SWIM_SENIOR_BASELINES, ...SWIM_YOUTH_BASELINES].forEach((row) => {
    out[row.id] = row;
  });
  return out;
})();

function readBaseline(key, settings) {
  const row = BASELINE_INDEX[key];
  const fallbackDuration = row?.default_duration_seconds ?? 90;
  const fallbackHeats = row?.default_heats ?? 2;
  // localStorage round-trips integers as integers (saveSettings spreads
  // raw values), so no parseInt needed; nullish-coalesce protects
  // against missing keys on fresh installs.
  return {
    default_duration_seconds: settings[`${key}_duration`] ?? fallbackDuration,
    default_heats: settings[`${key}_heats`] ?? fallbackHeats,
  };
}

/**
 * Toggle-chip button. Outline when unselected, primary when selected.
 * Keeps consistent sizing with the existing shadcn Button so the
 * builder layout doesn't bounce between states.
 */
function ToggleChip({ selected, onClick, children, dataTestId }) {
  return (
    <Button
      type="button"
      size="sm"
      variant={selected ? 'default' : 'outline'}
      onClick={onClick}
      className="h-8 px-3 text-xs"
      data-testid={dataTestId}
    >
      {children}
    </Button>
  );
}

export default function BulkEventBuilder({ poolUnit, swimPreset, onAdd, vocab }) {
  // Scholastic mode (HS / MS dual meets) → Varsity / JV / Freshman
  // chips. Summer-club mode → numeric age brackets. Read the preset
  // once at mount; the parent remounts us via `key` when the preset
  // switches, so this derivation never needs to re-run.
  const isScholastic = swimPreset === 'standard_hs';
  const standardAgeGroups = isScholastic ? SCHOLASTIC_DIVISIONS : YOUTH_CLUB_AGE_GROUPS;

  // Builder selections — all local to this component. We push events
  // upward via `onAdd` rather than mutating shared state directly.
  const [distance, setDistance] = useState(50);
  const [stroke, setStroke] = useState('Freestyle');
  // Gender chips start with Girls + Boys (the canonical dual-meet
  // pair); Mixed is opt-in for coed / open events.
  const [genders, setGenders] = useState(['Girls', 'Boys']);
  const [selectedAgeGroups, setSelectedAgeGroups] = useState(() => (
    // Sensible defaults per mode: scholastic dual meets usually run
    // Varsity + JV; summer-club meets start with the two youngest
    // brackets (typical opening-flight tradition).
    isScholastic ? ['Varsity', 'JV'] : ['8 & Under', '9-10']
  ));
  const [customAgeGroups, setCustomAgeGroups] = useState([]);
  const [customAgeInput, setCustomAgeInput] = useState('');

  const strokeMeta = STROKE_OPTIONS.find((s) => s.value === stroke) || STROKE_OPTIONS[0];

  // Live event-count for the "Add X Events" button label —
  // genders × age-groups, since swim tiles are mutually exclusive
  // (Girls / Boys / Mixed) and each permutation becomes its own row.
  const eventCount = genders.length * selectedAgeGroups.length;

  const previewName = useMemo(
    () => buildEventName(distance, stroke, poolUnit),
    [distance, stroke, poolUnit],
  );

  const toggleGender = (g) => {
    setGenders((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]));
  };

  const toggleAgeGroup = (ag) => {
    setSelectedAgeGroups((prev) => (prev.includes(ag) ? prev.filter((x) => x !== ag) : [...prev, ag]));
  };

  const addCustomAge = () => {
    const tag = customAgeInput.trim();
    if (!tag) return;
    // Dedupe against both the active standard list and existing custom
    // tags so the user can't shadow a built-in chip with a misspelled copy.
    if (!standardAgeGroups.includes(tag) && !customAgeGroups.includes(tag)) {
      setCustomAgeGroups((prev) => [...prev, tag]);
    }
    // Auto-select the freshly added tag so a single Enter press both
    // adds and includes it in the next bulk push.
    if (!selectedAgeGroups.includes(tag)) {
      setSelectedAgeGroups((prev) => [...prev, tag]);
    }
    setCustomAgeInput('');
  };

  const removeCustomAge = (tag) => {
    setCustomAgeGroups((prev) => prev.filter((x) => x !== tag));
    setSelectedAgeGroups((prev) => prev.filter((x) => x !== tag));
  };

  const handleBulkAdd = () => {
    if (eventCount === 0) return;
    const settings = getSettings();
    const name = buildEventName(distance, stroke, poolUnit);
    const eventType = strokeMeta.isRelay ? 'relay' : (distance >= 200 ? 'distance' : 'sprint');
    const out = [];
    const tsBase = Date.now();
    let serial = 0;

    // Loop order (Feb 2026): age groups OUTER, genders INNER. This
    // produces the sequential rhythm coaches actually script heat
    // sheets in — "9-10 Girls, 9-10 Boys, 11-12 Girls, 11-12 Boys"
    // — rather than the legacy "all Girls then all Boys" grouping.
    // Each row stamps EXACTLY ONE of showGirls / showBoys / combined
    // as true (via GENDER_FLAGS) because Swim event tiles are
    // mutually exclusive.
    selectedAgeGroups.forEach((ageGroup) => {
      const baselineKey = pickBaselineKey({
        distance,
        isRelay: strokeMeta.isRelay,
        ageGroup,
      });
      const baseline = readBaseline(baselineKey, settings);
      const durationStr = secondsToMMSS(baseline.default_duration_seconds);
      const heats = baseline.default_heats;
      genders.forEach((gender) => {
        const flags = GENDER_FLAGS[gender];
        // Stable-ish id: timestamp + per-bulk-serial guarantees no
        // collision inside a single bulk-add tap.
        serial += 1;
        out.push({
          id: `bulk-${tsBase}-${serial}`,
          name,
          type: eventType,
          eventId: `bulk_${distance}_${stroke}_${ageGroup}_${gender}`
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '_'),
          enabled: true,
          ...flags,
          ageGroup,
          girlsHeats: heats,
          boysHeats: heats,
          girlsDuration: durationStr,
          boysDuration: durationStr,
          defaultGirlsDuration: durationStr,
          defaultBoysDuration: durationStr,
          girlsOrder: 0,
          boysOrder: 0,
        });
      });
    });

    onAdd(out);
  };

  const allAgeGroups = [...standardAgeGroups, ...customAgeGroups];

  return (
    <Card className="p-4 mb-6" data-testid="bulk-event-builder">
      <h3 className="font-medium mb-1">Bulk {vocab.event} Builder</h3>
      <p className="text-xs text-muted-foreground mb-4">
        Pick a distance + stroke, then any combination of genders and
        age groups. We&apos;ll generate one flat {vocab.event.toLowerCase()}{' '}
        row per permutation so the schedule engine sees the same
        standard shape as everything else.
      </p>

      {/* Base event — Distance + Stroke */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="space-y-1">
          <Label className="text-xs">Distance</Label>
          <Select value={String(distance)} onValueChange={(v) => setDistance(parseInt(v, 10))}>
            <SelectTrigger data-testid="bulk-distance-trigger" className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DISTANCE_OPTIONS.map((d) => (
                <SelectItem key={d} value={String(d)} data-testid={`bulk-distance-${d}`}>
                  {/* Render the 500/400 swap directly in the menu so
                      the coach sees what they'll get before picking */}
                  {d === 500 && normalizePoolUnit(poolUnit) === 'meters' ? '400' : d}
                  {getDistanceSuffix(poolUnit)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Stroke</Label>
          <Select value={stroke} onValueChange={setStroke}>
            <SelectTrigger data-testid="bulk-stroke-trigger" className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STROKE_OPTIONS.map((s) => (
                <SelectItem key={s.value} value={s.value} data-testid={`bulk-stroke-${s.value}`}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Preview of the event name that will be stamped onto each row */}
      <div className="mb-4 px-3 py-2 rounded-md bg-muted/50 text-xs">
        <span className="text-muted-foreground">Preview: </span>
        <span className="font-medium" data-testid="bulk-preview-name">{previewName}</span>
      </div>

      {/* Gender chips */}
      <div className="space-y-2 mb-4">
        <Label className="text-xs">Genders</Label>
        <div className="flex gap-2" data-testid="bulk-gender-chips">
          {GENDER_OPTIONS.map((g) => (
            <ToggleChip
              key={g}
              selected={genders.includes(g)}
              onClick={() => toggleGender(g)}
              dataTestId={`bulk-gender-${g.toLowerCase()}`}
            >
              {g}
            </ToggleChip>
          ))}
        </div>
      </div>

      {/* Age-group chips + custom input. In scholastic mode the section
          is relabeled "Divisions" and the input placeholder hints at
          scholastic-style tags. */}
      <div className="space-y-2 mb-4">
        <Label className="text-xs">{isScholastic ? 'Divisions' : 'Age Groups'}</Label>
        <div className="flex flex-wrap gap-2" data-testid="bulk-age-group-chips">
          {allAgeGroups.map((ag) => {
            const isCustom = customAgeGroups.includes(ag);
            return (
              <div key={ag} className="flex items-center">
                <ToggleChip
                  selected={selectedAgeGroups.includes(ag)}
                  onClick={() => toggleAgeGroup(ag)}
                  dataTestId={`bulk-age-group-${ag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {ag}
                </ToggleChip>
                {isCustom && (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => removeCustomAge(ag)}
                    className="h-8 w-6 -ml-1"
                    aria-label={`Remove custom age group ${ag}`}
                    data-testid={`bulk-remove-age-${ag.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex gap-2 pt-1">
          <Input
            type="text"
            placeholder={isScholastic
              ? '+ Custom Division (e.g. Frosh/Soph, Senior)'
              : '+ Custom Age (e.g. 9-12, 11-14)'}
            value={customAgeInput}
            onChange={(e) => setCustomAgeInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomAge();
              }
            }}
            className="h-8 text-xs"
            data-testid="bulk-custom-age-input"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={addCustomAge}
            disabled={!customAgeInput.trim()}
            className="h-8 text-xs whitespace-nowrap"
            data-testid="bulk-add-custom-age-btn"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add
          </Button>
        </div>
      </div>

      {/* Generate button — disabled until both gender + age have ≥1 pick */}
      <Button
        type="button"
        onClick={handleBulkAdd}
        disabled={eventCount === 0}
        className="w-full"
        data-testid="bulk-add-events-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add {eventCount} {vocab.event}{eventCount === 1 ? '' : 's'}
      </Button>
      {eventCount > 0 && (
        <p className="text-[11px] text-muted-foreground text-center mt-2">
          {genders.length} gender{genders.length === 1 ? '' : 's'} ×{' '}
          {selectedAgeGroups.length}{' '}
          {isScholastic
            ? `division${selectedAgeGroups.length === 1 ? '' : 's'}`
            : `age group${selectedAgeGroups.length === 1 ? '' : 's'}`}
          {' '}= {eventCount} row{eventCount === 1 ? '' : 's'} of &quot;{previewName}&quot;
        </p>
      )}
    </Card>
  );
}
